import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import './CourseSubjectManagerModal.css';

const IconPencil = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconX = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconPlus = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CourseSubjectManagerModal = ({ isOpen, onClose, onSubjectsChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [createPopoverOpen, setCreatePopoverOpen] = useState(false);
  const [createPopoverRect, setCreatePopoverRect] = useState(null);
  const createPopoverRef = useRef(null);
  const createPlusRef = useRef(null);
  const createTitleInputRef = useRef(null);

  const updateCreatePopoverPosition = useCallback(() => {
    const btn = createPlusRef.current;
    if (!btn || !createPopoverOpen) return;
    const rect = btn.getBoundingClientRect();
    const margin = 12;
    const gap = 8;
    const w = Math.min(300, window.innerWidth - margin * 2);
    let left = rect.right - w;
    if (left < margin) left = margin;
    if (left + w > window.innerWidth - margin) left = Math.max(margin, window.innerWidth - w - margin);
    let top = rect.bottom + gap;
    const estHeight = 300;
    if (top + estHeight > window.innerHeight - margin && rect.top > estHeight + gap + margin) {
      top = rect.top - estHeight - gap;
    }
    top = Math.max(margin, top);
    if (top + estHeight > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - margin - estHeight);
    }
    setCreatePopoverRect({ top, left, width: w });
  }, [createPopoverOpen]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/taxonomy/course-subjects', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Could not load subjects.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchItems();
      setNewTitle('');
      setNewDescription('');
      setEditingId(null);
      setCreatePopoverOpen(false);
    }
  }, [isOpen, fetchItems]);

  useLayoutEffect(() => {
    if (!createPopoverOpen) {
      setCreatePopoverRect(null);
      return;
    }
    updateCreatePopoverPosition();
    window.addEventListener('resize', updateCreatePopoverPosition);
    window.addEventListener('scroll', updateCreatePopoverPosition, true);
    return () => {
      window.removeEventListener('resize', updateCreatePopoverPosition);
      window.removeEventListener('scroll', updateCreatePopoverPosition, true);
    };
  }, [createPopoverOpen, updateCreatePopoverPosition, loading, items.length]);

  useEffect(() => {
    if (!createPopoverOpen || !createPopoverRect) return;
    const raf = requestAnimationFrame(() => {
      createTitleInputRef.current?.focus();
    });
    const onPointerDown = (e) => {
      if (createPopoverRef.current?.contains(e.target)) return;
      if (createPlusRef.current?.contains(e.target)) return;
      setCreatePopoverOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [createPopoverOpen, createPopoverRect]);

  const handleCreate = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/taxonomy',
        {
          title,
          description: newDescription.trim() || null,
          type: 'Course-Subject',
          concentrations: [],
          parents: []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewTitle('');
      setNewDescription('');
      setCreatePopoverOpen(false);
      await fetchItems();
      onSubjectsChanged?.();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to create subject.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditTitle(item.title || '');
    setEditDescription(item.description || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditDescription('');
  };

  const handleSaveEdit = async (id) => {
    const title = editTitle.trim();
    if (!title) return;
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/taxonomy/${id}`,
        {
          title,
          description: editDescription.trim() || null,
          concentrations: [],
          parents: []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      cancelEdit();
      await fetchItems();
      onSubjectsChanged?.();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update subject.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        'Remove this subject from the list? It will be marked deleted in the database; existing courses keep the stored ID until you pick another subject.'
      )
    ) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/taxonomy/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (editingId === id) cancelEdit();
      await fetchItems();
      onSubjectsChanged?.();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to remove subject.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const createPopoverPortal =
    createPopoverOpen && createPopoverRect
      ? createPortal(
          <div
            ref={createPopoverRef}
            className="course-subject-create-popover course-subject-create-popover--fixed"
            style={{
              position: 'fixed',
              top: createPopoverRect.top,
              left: createPopoverRect.left,
              width: createPopoverRect.width,
              zIndex: 12000
            }}
            role="dialog"
            aria-label="New subject"
          >
            <div className="course-subject-create-popover__head">
              <span className="course-subject-create-popover__head-title">New subject</span>
              <button
                type="button"
                className="course-subject-create-popover__close"
                onClick={() => setCreatePopoverOpen(false)}
                aria-label="Close"
                disabled={saving}
              >
                ×
              </button>
            </div>
            <div className="course-subject-create-popover__body">
              <div className="course-subject-modal__form-row course-subject-create-popover__row">
                <label htmlFor="csm-new-title">Title</label>
                <input
                  ref={createTitleInputRef}
                  id="csm-new-title"
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Algebra I"
                  disabled={saving}
                />
              </div>
              <div className="course-subject-modal__form-row course-subject-create-popover__row">
                <label htmlFor="csm-new-desc">Description (optional)</label>
                <textarea
                  id="csm-new-desc"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  rows={2}
                  placeholder="Short notes or codes"
                  disabled={saving}
                />
              </div>
              <button
                type="button"
                className="course-subject-modal__btn course-subject-modal__btn--primary course-subject-create-popover__submit"
                onClick={handleCreate}
                disabled={saving || !newTitle.trim()}
              >
                Create
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="course-subject-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="course-subject-modal-title">
      {createPopoverPortal}
      <div className="course-subject-modal">
        <div className="course-subject-modal__header">
          <div>
            <h2 id="course-subject-modal-title">Course subjects</h2>
            <p className="course-subject-modal__subtitle">
              Taxonomy type <code>Course-Subject</code> — used for TK–12 courses in Course Builder.
            </p>
          </div>
          <button type="button" className="course-subject-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {error ? <div className="course-subject-modal__error">{error}</div> : null}

        <section className="course-subject-modal__list-wrap">
          <div className="course-subject-modal__list-toolbar">
            <span className="course-subject-modal__count" aria-live="polite">
              {loading ? '…' : `${items.length} subject${items.length === 1 ? '' : 's'}`}
            </span>
            <button
              ref={createPlusRef}
              type="button"
              className="course-subject-modal__plus-btn"
              onClick={() => setCreatePopoverOpen((o) => !o)}
              disabled={saving}
              title="Create subject"
              aria-label="Create new subject"
              aria-expanded={createPopoverOpen}
            >
              <IconPlus />
            </button>
          </div>

          {loading ? (
            <div className="course-subject-modal__spinner">
              <LoadingSpinner />
            </div>
          ) : items.length === 0 ? (
            <p className="course-subject-modal__empty">No subjects yet. Use + to add one.</p>
          ) : (
            <ul className="course-subject-modal__list">
              {items.map((item) => {
                const descTrimmed = (item.description || '').trim();
                const subline = descTrimmed || 'No Description';
                return (
                  <li key={item.id} className="course-subject-modal__item">
                    {editingId === item.id ? (
                      <div className="course-subject-modal__edit">
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          disabled={saving}
                        />
                        <textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          rows={2}
                          disabled={saving}
                        />
                        <div className="course-subject-modal__edit-actions">
                          <button
                            type="button"
                            className="course-subject-modal__btn course-subject-modal__btn--primary"
                            onClick={() => handleSaveEdit(item.id)}
                            disabled={saving || !editTitle.trim()}
                          >
                            Save
                          </button>
                          <button type="button" className="course-subject-modal__btn" onClick={cancelEdit} disabled={saving}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="course-subject-modal__item-body">
                          <span className="course-subject-modal__item-title" title={item.id}>
                            {item.title}
                          </span>
                          <span
                            className={
                              descTrimmed
                                ? 'course-subject-modal__item-desc'
                                : 'course-subject-modal__item-desc course-subject-modal__item-desc--empty'
                            }
                          >
                            {subline}
                          </span>
                        </div>
                        <div className="course-subject-modal__item-actions">
                          <button
                            type="button"
                            className="course-subject-modal__icon-btn"
                            onClick={() => startEdit(item)}
                            disabled={saving}
                            title="Edit"
                            aria-label={`Edit ${item.title || 'subject'}`}
                          >
                            <IconPencil />
                          </button>
                          <button
                            type="button"
                            className="course-subject-modal__icon-btn course-subject-modal__icon-btn--danger"
                            onClick={() => handleDelete(item.id)}
                            disabled={saving}
                            title="Remove (mark deleted)"
                            aria-label={`Remove ${item.title || 'subject'}`}
                          >
                            <IconX />
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <div className="course-subject-modal__footer">
          <button type="button" className="course-subject-modal__btn course-subject-modal__btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseSubjectManagerModal;
